<script lang="ts">
    import {
        categories,
        type Category,
        type CustomCategoryElement, customCategoryElements, type CustomCategoryName, customCategoryNames,
        type Entry,
        type Status,
        statuses,
        type Tag,
        tags
    } from "../store";
    import {createEventDispatcher} from "svelte";
    import {format} from "date-fns";

    export let entry: Entry;
    let titleExists = entry.title.trim()!=='';

    const dispatch = createEventDispatcher();

    function getCategory(category: string) {
        return $categories.get(category) as Category;
    }

    function getTag(tag: string) {
        return $tags.get(tag) as Tag;
    }

    function getStatus(status: string) {
        return $statuses.get(status) as Status;
    }

    let customCategoryMap = new Map();
    let temp = Array.from(customCategoryMap.keys());
    $: if (entry) {
        customCategoryMap = new Map();
        entry.customCategories.forEach((element) => {
            const elementObj = $customCategoryElements.get(element) as CustomCategoryElement;
            if (!customCategoryMap.get(elementObj.parentId)) {
                customCategoryMap.set(elementObj.parentId, [elementObj]);
            } else {
                customCategoryMap.get(elementObj.parentId).push(elementObj);
            }
        });
        temp = Array.from(customCategoryMap.keys());
    }

    function getParent(parentId: string) {
        return $customCategoryNames.get(parentId) as CustomCategoryName;
    }

    function getElements(id: string) {
        return customCategoryMap.get(id) as CustomCategoryElement[];
    }

</script>

<div class="modal-header justify-content-between">
    <div class="d-flex align-items-center justify-content-between me-1">
        {#if titleExists}
            <h5 class="modal-title fw-bold" data-testid="modal-title">{entry.title}</h5>
        {/if}
    </div>
    <div>
        <button class="modal-button" on:click|stopPropagation={() => dispatch('edit')}>
            <svg width="24" height="20">
                <use href="#icon-edit-simple"/>
            </svg>
        </button>
        <button class="modal-button" on:click|stopPropagation={() => dispatch('showPopup')} data-testid="delete-button">
            <svg width="24" height="20">
                <use href="#icon-trash" />
            </svg>
        </button>

        <button class="modal-button" on:click|stopPropagation={() => dispatch('close')}>
            <svg width="24" height="20">
                <use href="#icon-close"/>
            </svg>
        </button>
    </div>
</div>

<div class="modal-scrollable d-flex flex-column h-100 overflow-auto">

    <div class="modal-body flex-grow-1">
        <p class="modal-text">{entry.value}</p>
    </div>

    <div class="modal-footer p-3 mb-0">
        <!-- Categories -->
        {#if entry.categories.length > 0}
            <div class="d-flex mb-1">
                <div class="types d-inline-flex align-items-center rounded flex-wrap">
                    <div class="title fw-bold">
                        Categories:
                    </div>
                    {#each entry.categories as category}
                        <div class="chip px-3 py-2 rounded d-flex align-items-center text-white fw-bold">
                            {getCategory(category).name}
                        </div>
                    {/each}
                </div>
            </div>
        {/if}

        <!-- Tags -->
        {#if entry.tags.length > 0}
            <div class="d-flex mb-1">
                <div class="types d-inline-flex align-items-center rounded flex-wrap">
                    <div class="title fw-bold">
                        Tags:
                    </div>
                    {#each entry.tags as tag}
                        <div class="chip px-3 py-2 rounded d-flex align-items-center text-white fw-bold">
                            {getTag(tag).name}
                        </div>
                    {/each}
                </div>
            </div>
        {/if}

        <!-- Statuses -->
        {#if entry.statuses.length > 0}
            <div class="d-flex mb-1">
                <div class="types d-inline-flex align-items-center rounded flex-wrap">
                    <div class="title fw-bold">
                        Statuses:
                    </div>
                    {#each entry.statuses as status}
                        <div class="chip px-3 py-2 rounded d-flex align-items-center text-white fw-bold">
                            {getStatus(status).name}
                        </div>
                    {/each}
                </div>
            </div>
        {/if}

        <!-- Custom? -->
        {#if temp.length > 0}
            {#each temp as parent}
                <div class="d-flex mb-1">
                    <div class="types d-inline-flex align-items-center rounded flex-wrap">
                        <div class="title fw-bold">
                            {getParent(parent).name}:
                        </div>
                        {#each getElements(parent) as element}
                            <div class="chip px-3 py-2 rounded d-flex align-items-center text-white fw-bold">
                                {element.name}
                            </div>
                        {/each}
                    </div>
                </div>
            {/each}
        {/if}

        <!-- Date -->
        <div class="d-flex">
            <div class="mt-2 d-inline-flex align-items-center rounded flex-wrap">
                <p class="title fw-bold">{format(entry.timestamp, "MMM d, yyyy")}</p>
            </div>
        </div>
    </div>
</div>


<style>
    .modal-header {
        display: flex;
        padding-bottom: 0.7rem;
        align-content: center;
        border-bottom: 1px solid #ccc;
    }

    .modal-title {
        margin: 0;
    }

    .modal-text {
        white-space: normal;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        line-height: 1.75rem;
        text-align: justify;
        font-size: 1rem;
        color: #333;
        letter-spacing: 0.02em;
        margin: 0 0 1rem 0;
    }

    .modal-scrollable {
        height: 100vh;
        display: flex;
        flex-direction: column;
        overflow-y: auto;

        /* Hide scrollbar */
        scrollbar-width: none; /* Firefox */
        &::-webkit-scrollbar { /* Chrome and other webkit browswers */
            display: none;
        }
    }

    .modal-body {
        flex-grow: 1;
    }

    .modal-button {
        background: none;
        border: none;
        font-size: 1.5rem;
        cursor: pointer;
        transition: transform 0.05s ease-in-out;
    }

    .modal-button:hover {
        transform: scale(1.1);
    }

    .title {
        gap: 0.15rem;
    }

    .types {
        background-color: #d4dae5;
        margin-bottom: 0.5rem;
        padding: 0.15rem 0.25rem;
        margin-left: 0.5rem;
    }

    .chip {
        margin-left: 0.25rem;
        height: 2rem;
        background-color: #6d7583;
    }

    .text-border {
        padding: 0.375rem 0.75rem;
        box-sizing: border-box;
        transition: box-shadow 0.2s ease-in-out;
    }

    .modal-footer {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
    }

    .text-border:focus {
        box-shadow: 0 0 0 0.2rem rgba(79, 141, 255, 0.34);
        outline: none;
    }
</style>